import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';

import { SignalRApi } from '@/app/app/services/api/signal-r.api';
import { environment } from '@/app/environments/environment';
import { isNullOrEmpty } from '@/shared/lib/core/utils/data-validation.util';
import { MessageService } from '@/shared/lib/services/communications/message.service';
import { NotificationStore } from '@/shared/lib/stores/notification.store';

import { SignalRConnectionInfo } from '../../interfaces/account/signal-r-connection-info';

@Service()

export class SignalRService {
  message = inject(MessageService);
  http = inject(HttpClient);
  notificationStore = inject(NotificationStore);
  signalRApi = inject(SignalRApi);

  private hubConnection: HubConnection | undefined;
  private signalRConnectionInfo: SignalRConnectionInfo | undefined;
  private activeHubList: string[] = [];
  private isInitializing = false;

  public initialize() {
    return new Promise<boolean>((resolve) => {
      if (!this.isInitializing && isNullOrEmpty(this.hubConnection)) {
        this.isInitializing = true;
        this.signalRApi.getSignalRToken().subscribe({
          next: result => {
            this.message.add('SignalR.initialize()', 'gotToken');
            this.signalRConnectionInfo = result;
            this.startSignalR().then(() => {
              this.isInitializing = false;
              this.resubscribeActiveHubs();

              resolve(this.hubConnection?.state === signalR.HubConnectionState.Connected);
            });
          },
          // Reset so a later initialize() can retry instead of waiting on this attempt forever
          error: error => {
            this.message.warn('SignalR.initialize()', error);
            this.isInitializing = false;
            resolve(false);
          },
        });
      } else {
        resolve(true);
      }
    });
  }

  public async startSignalR(): Promise<void> {
    this.notificationStore.setSignalRConnectionState(0);
    await this.stopSignalR();
    this.notificationStore.setSignalRConnectionState(1);

    const options: signalR.IHttpConnectionOptions = {
      accessTokenFactory: () => this.signalRConnectionInfo?.accessToken ?? '',
    };

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.signalRConnectionInfo?.url ?? '', options)
      .configureLogging(environment.production ? signalR.LogLevel.Error : signalR.LogLevel.Debug)
      .withAutomaticReconnect()
      .build();

    await this.hubConnection.start().then(() => {
      this.notificationStore.setSignalRConnectionState(2);
    });

    this.hubConnection.onclose(() => {
      this.notificationStore.setSignalRConnectionState(0);

      if (this.hubConnection) {
        this.hubConnection
          .start()
          .then(() => {
            this.resubscribeActiveHubs();
            this.message.add('startSignalR', 'Hub Connection Restarted.');
            this.notificationStore.setSignalRConnectionState(2);
          })
          .catch(err => {
            this.message.error('startSignalR', err);
            this.notificationStore.setSignalRConnectionState(0);
          });
      }
    });
  }

  public async stopSignalR(): Promise<void> {
    this.notificationStore.setSignalRConnectionState(0);
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection.off('notify');
      return await this.hubConnection.stop();
    }
    return;
  }

  public subscribeToDeploymentHub(deploymentId: number) {
    const hubName = `deployment_${deploymentId}`;
    if (isNullOrEmpty(this.activeHubList.find(f => f === hubName))) {
      this.initialize().then(() => this.onHub(hubName));
    }
    this.notificationStore.setSignalRConnectionState(2);
  }

  public subscribeRealtimeHub(correlationId: string) {
    const hubName = `realtime_${correlationId}`;
    if (isNullOrEmpty(this.activeHubList.find(f => f === hubName))) {
      this.initialize().then(() => this.onHub(hubName));
    }
    this.notificationStore.setSignalRConnectionState(2);
  }

  public unsubscribeRealtimeHub(correlationId: string) {
    const hubName = `realtime_${correlationId}`;
    this.hubConnection?.off(hubName);
  }

  public unsubscribeActiveHubs() {
    this.activeHubList.forEach((hub) => {
      this.hubConnection?.off(hub);
    });

    this.activeHubList = [];
    this.notificationStore.setSignalRConnectionState(0);
  }

  private resubscribeActiveHubs() {
    this.notificationStore.setSignalRConnectionState(0);
    this.activeHubList.forEach((hub) => {
      this.message.add('resubscribeActiveHubs', hub);
      this.hubConnection?.off(hub);
      this.onHub(hub);
    });
    this.notificationStore.setSignalRConnectionState(2);
  }

  private onHub(hubName: string): void {
    if (this.activeHubList.find(f => f === hubName) === undefined) {
      this.hubConnection?.on(hubName, data => {
        this.notificationStore.setSignalRConnectionState(2);
        this.notificationStore.setActionUnread(data);
      });
      this.activeHubList.push(hubName);
    }
  }
}
